import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import API_PATH from "~/utils/api-routes";
import { AUTH_COOKIE_TOKEN } from "~/utils/constant";
import { getCookie } from "~/utils/cookies";
import { createSmkApi, setLatestOtpToken } from "~/utils/smkInterseptor";

export interface UserProfile {
  all_other_noti: boolean;
  birth_year: number | null;
  city: string | null;
  country_code: string | null;
  daily_darshan_noti: boolean;
  email: string | null;
  firstname: string | null;
  gender: string | null;
  id: number;
  is_smk: number; // Could be boolean, but your data shows it as 0/1
  language: string;
  live_streaming: boolean;
  middlename: string | null;
  new_releases_noti: boolean;
  status: string; // e.g., "active"
  surname: string | null;
}

interface ProfileState {
  userMobileNumber: string | null;
  userCountryPhoneCode: string | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
}

const initialState: ProfileState = {
  userMobileNumber: null,
  userCountryPhoneCode: null,
  userProfile: null,
  loading: false,
  error: null,
};

interface profilePayload {
  // country_code: string | null;
  // daily_darshan_noti: boolean | null;
  // new_releases_noti: boolean | null;
  // live_streaming: boolean | null;
  // all_other_noti: boolean | null;
  firstname: string | null;
  middlename?: string | null;
  surname: string | null;
  email: string | null;
  birth_year: number | null;
  city: string | null;
  gender: string | null;
  is_smk?: number; // Optional, only if needed
}

const smkService = createSmkApi();
// create a async thunk for get and add profile and get data as payload
export const getProfileThunk = createAsyncThunk("profile/get", async (_, { rejectWithValue }) => {
  try {
    const token = getCookie(AUTH_COOKIE_TOKEN);
    setLatestOtpToken(token ?? "");
    const response = await smkService.get(API_PATH.CONTENT_USER);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data.message || "An error occurred while editing profile";
    return rejectWithValue(message);
  }
});

export const editProfileThunk = createAsyncThunk("profile/edit", async ({ profileData }: { profileData: profilePayload }, { rejectWithValue }) => {
  try {
    const token = getCookie(AUTH_COOKIE_TOKEN);
    setLatestOtpToken(token ?? "");
    const response = await smkService.put(`${API_PATH.CONTENT_USER}`, profileData);
    return response.data.data;
  } catch (error) {
    return rejectWithValue("Failed to add profile");
  }
});

//add profile slice
export const addProfileThunk = createAsyncThunk("profile/add", async (profileData: profilePayload, { rejectWithValue }) => {
  try {
    const response = await smkService.post(API_PATH.CONTENT_USER, profileData);
    return response.data.data;
  } catch (error) {
    return rejectWithValue("Failed to add profile");
  }
});

// delete profile thunk
export const deleteProfileThunk = createAsyncThunk("profile/delete", async (reason: string, { rejectWithValue }) => {
  try {
    const token = getCookie(AUTH_COOKIE_TOKEN);
    setLatestOtpToken(token ?? "");
    const response = await smkService.delete(`${API_PATH.CONTENT_USER}`, { data: { reason } });
    return response.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data.message || "An error occurred while deleting profile";
    return rejectWithValue(message);
  }
});

export const getBhaktiToken = createAsyncThunk("profile/getBhaktiToken", async () => {
  const token = getCookie(AUTH_COOKIE_TOKEN);
  setLatestOtpToken(token ?? "");
  const res = await smkService.get(API_PATH.BHAKTI_TOKEN);
  return res.data.data;
});

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    setUserMobileNumber: (state, action) => {
      state.userMobileNumber = action.payload;
    },
    setUserCountryPhoneCode: (state, action) => {
      state.userCountryPhoneCode = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProfileThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProfileThunk.fulfilled, (state, action) => {
        state.userProfile = action.payload;
        state.loading = false;
      })
      .addCase(getProfileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(editProfileThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editProfileThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.userProfile = action.payload;
        state.error = null;
      })
      .addCase(editProfileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteProfileThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProfileThunk.fulfilled, (state) => {
        state.loading = false;
        state.userProfile = null;
        state.error = null;
      })
      .addCase(deleteProfileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});
export const { setUserMobileNumber, setUserCountryPhoneCode, setError } = profileSlice.actions;
export default profileSlice.reducer;
